import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Icon, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TOptions } from 'i18next';
import { makeRequest } from '../requestUtils';

type SyncText = (key: string, options?: TOptions) => string;
export type AssetSyncMode = 'Update' | 'Stop' | 'CreateLicense' | 'CreateItem';
export interface SyncPreview {
  previewToken: string;
  differences: Array<{ field: string; salesforceValue: string | null; webkuaValue: string | null }>;
  clearedFields: string[];
  assetSync?: { mode: AssetSyncMode; lastUsedDate2026: string | null };
  billingCityMatchingPlace?: string | null;
}
interface SyncSubmission {
  orderId: string;
  status: 'Submitted' | 'Recorded';
}
interface SyncError {
  code: string;
  orderId?: string;
}
const errorCodes = ['InvalidPreview', 'SourceChanged', 'AmbiguousLink', 'NoLongerEligible', 'NoDifferences',
  'SellerUnresolved', 'UnknownCountry', 'OperatorUnresolved', 'PendingOrder', 'OrderConflict', 'DispatchFailed', 'IncompleteSource',
  'UnsupportedDifference', 'UnsafeSource', 'EmptySource', 'MultipleTargets', 'DiscountUnsupported', 'ProductUnresolved',
  'InactiveTarget', 'UnsafeTarget', 'InvalidQuantity', 'ParentUnresolved'];

interface SyncConfiguration<TRow> {
  entity: 'accounts' | 'contracts' | 'assets';
  namespace: string;
  rowKey: (row: TRow) => string;
  identityField: string;
  identity: (row: TRow) => string | null;
  label?: (row: TRow) => string | null;
  target: (row: TRow) => object;
  visible: (row: TRow) => boolean;
  blockReason: (row: TRow) => string | null;
  blockedTooltip: (row: TRow, text: SyncText) => string;
  differences: (row: TRow) => SyncPreview['differences'];
  usesClears: boolean;
  clearedFields: (row: TRow) => string[];
  warningText?: (differences: SyncPreview['differences'], text: SyncText) => string;
  validatePreview?: (preview: SyncPreview) => boolean;
  previewContent?: (preview: SyncPreview, text: SyncText) => ReactNode;
  tooltip?: (row: TRow, text: SyncText) => string | null;
  rowWarning?: (row: TRow, text: SyncText) => string | null;
  previewWarning?: (preview: SyncPreview, text: SyncText) => string | null;
  fieldName: (field: string, text: SyncText) => string;
  value: (field: string, value: string | null, text: SyncText) => string;
  valueColumns: [string, string];
}

function syncError(error: unknown): SyncError {
  if (isRecord(error) && error.isAxiosError === true && isRecord(error.response) && isRecord(error.response.data)) {
    const data = error.response.data;
    return {
      code: typeof data.code === 'string' ? data.code : 'RequestFailed',
      orderId: typeof data.orderId === 'string' && data.orderId.trim() ? data.orderId : undefined,
    };
  }
  return { code: 'RequestFailed' };
}
const isRecord = (value: unknown): value is Record<string, unknown> => value != null && typeof value === 'object';
function isPreview(value: unknown): value is SyncPreview {
  return isRecord(value) && typeof value.previewToken === 'string' && !!value.previewToken.trim()
    && Array.isArray(value.clearedFields)
    && value.clearedFields.every((field: unknown) => typeof field === 'string')
    && (value.billingCityMatchingPlace == null || typeof value.billingCityMatchingPlace === 'string')
    && (value.assetSync === undefined || (isRecord(value.assetSync) &&
      ['Update', 'Stop', 'CreateLicense', 'CreateItem'].includes(String(value.assetSync.mode)) &&
      (value.assetSync.lastUsedDate2026 === null || (typeof value.assetSync.lastUsedDate2026 === 'string' &&
        value.assetSync.lastUsedDate2026.startsWith('2026-') && Number.isFinite(Date.parse(value.assetSync.lastUsedDate2026))))))
    && Array.isArray(value.differences) && value.differences.length > 0
    && value.differences.every((difference: unknown) => isRecord(difference) && typeof difference.field === 'string'
      && (difference.salesforceValue === null || typeof difference.salesforceValue === 'string')
      && (difference.webkuaValue === null || typeof difference.webkuaValue === 'string'));
}
function isSubmission(value: unknown): value is SyncSubmission {
  return isRecord(value) && typeof value.orderId === 'string' && !!value.orderId.trim()
    && (value.status === 'Submitted' || value.status === 'Recorded');
}

export function useReconciliationSyncControls<TRow>(configuration: SyncConfiguration<TRow>, onRefresh?: () => void) {
  const { t } = useTranslation();
  const text: SyncText = (key, options) => t(`${configuration.namespace}.${key}`,
    { ...options, returnObjects: false, returnDetails: false });
  const { rowKey } = configuration;
  const [selected, setSelected] = useState<TRow | null>(null);
  const [preview, setPreview] = useState<SyncPreview | null>(null);
  const [error, setError] = useState<SyncError | null>(null);
  const [submission, setSubmission] = useState<SyncSubmission | null>(null);
  const [recorded, setRecorded] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const open = async (row: TRow) => {
    if (inFlight.current || configuration.blockReason(row) || recorded[rowKey(row)]) return;
    inFlight.current = true;
    setBusy(true);
    setSelected(row);
    setPreview(null);
    setError(null);
    setSubmission(null);
    try {
      const result: unknown = await makeRequest(`salesforce/reconciliation/${configuration.entity}/sync/preview`, 'POST',
        configuration.target(row));
      const normalized = !configuration.usesClears && isRecord(result) ? { ...result, clearedFields: [] } : result;
      if (!isPreview(normalized) || !isRecord(result) || result[configuration.identityField] !== configuration.identity(row) ||
        (configuration.validatePreview && !configuration.validatePreview(normalized)))
        throw new Error('Invalid sync preview response.');
      setPreview(normalized);
    } catch (failure) {
      const result = syncError(failure);
      setError(result);
      if (result.orderId) setRecorded((current) => ({ ...current, [rowKey(row)]: result.orderId ?? '' }));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (inFlight.current || !selected || !preview || recorded[rowKey(selected)]) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const result: unknown = await makeRequest(`salesforce/reconciliation/${configuration.entity}/sync`, 'POST', {
        previewToken: preview.previewToken,
      });
      if (!isSubmission(result)) throw new Error('Invalid sync submission response.');
      setSubmission(result);
      setRecorded((current) => ({ ...current, [rowKey(selected)]: result.orderId }));
    } catch (failure) {
      const result = syncError(failure);
      setError(result);
      if (result.orderId) setRecorded((current) => ({ ...current, [rowKey(selected)]: result.orderId ?? '' }));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const close = () => {
    if (inFlight.current) return;
    setSelected(null);
    setPreview(null);
    setError(null);
    setSubmission(null);
  };
  const canRetry = error?.code === 'RequestFailed';
  const dialog = <Dialog open={selected != null} onClose={close} maxWidth="md" fullWidth>
    <DialogTitle>{text('sync.title', { number: selected
      ? configuration.label?.(selected) ?? configuration.identity(selected) : undefined })}</DialogTitle>
    <DialogContent>
      <Stack spacing={2}>
        <Typography variant="body2">{text('sync.description')}</Typography>
        {busy && <Box role="status"><CircularProgress size={24} /> {text('sync.loading')}</Box>}
        {error && <Alert severity="error">
          {text(`sync.errors.${errorCodes.includes(error.code) ? error.code : 'RequestFailed'}`)}
          {error.orderId && <Typography variant="body2">{text('sync.orderId', { id: error.orderId })}</Typography>}
        </Alert>}
        {submission && <Alert severity={submission.status === 'Submitted' ? 'info' : 'warning'}>
          {text(submission.status === 'Submitted' ? 'sync.submitted' : 'sync.recorded')}
          <Typography variant="body2">{text('sync.orderId', { id: submission.orderId })}</Typography>
        </Alert>}
        {preview && !submission && <>
          {configuration.previewContent?.(preview, text)}
          {preview.clearedFields.length > 0 && <Alert severity="warning">
            {configuration.warningText?.(preview.differences, text) ?? text('sync.clearWarning')}
            {!configuration.warningText && <Typography variant="body2">
              {preview.clearedFields.map((field) => configuration.fieldName(field, text)).join(', ')}
            </Typography>}
          </Alert>}
          {configuration.previewWarning?.(preview, text) && <Alert severity="warning">
            {configuration.previewWarning(preview, text)}
          </Alert>}
          <Table size="small" aria-label={text('sync.preview')}>
            <TableHead><TableRow>{['field', ...configuration.valueColumns].map((key) =>
              <TableCell key={key}>{text(key)}</TableCell>)}</TableRow></TableHead>
            <TableBody>{preview.differences.map((difference) => <TableRow key={difference.field}>
              <TableCell>{configuration.fieldName(difference.field, text)}</TableCell>
              <TableCell>{configuration.value(difference.field, difference.salesforceValue, text)}</TableCell>
              <TableCell>{configuration.value(difference.field, difference.webkuaValue, text)}</TableCell>
            </TableRow>)}</TableBody>
          </Table>
        </>}
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={close} disabled={busy}>{text('sync.close')}</Button>
      {onRefresh && (submission || error) && <Button onClick={() => { close(); onRefresh(); }} disabled={busy}>
        {text('sync.refresh')}
      </Button>}
      {selected && error && !error.orderId && !canRetry && <Button onClick={() => open(selected)} disabled={busy}>
        {text('sync.newPreview')}
      </Button>}
      {preview && !submission && !error?.orderId && (!error || canRetry) && <Button
        variant="contained" color={preview.clearedFields.length > 0 || configuration.previewWarning?.(preview, text) ? 'warning' : 'primary'}
        disabled={busy} onClick={confirm}>
        {text(canRetry ? 'sync.retry' : 'sync.confirm')}
      </Button>}
    </DialogActions>
  </Dialog>;

  const action = (row: TRow) => {
    if (!configuration.visible(row)) return null;
    const reason = configuration.blockReason(row);
    const allowed = reason == null;
    const clears = configuration.clearedFields(row);
    const rowWarning = configuration.rowWarning?.(row, text);
    const hasWarning = clears.length > 0 || !!rowWarning;
    const orderId = recorded[rowKey(row)];
    const differences = configuration.differences(row);
    const fields = [...new Set(differences
      .map((difference) => configuration.fieldName(difference.field, text)))].join(', ');
    const tooltip = !allowed ? configuration.blockedTooltip(row, text)
      : configuration.tooltip?.(row, text) ?? [text('sync.tooltip', { fields }), ...(clears.length > 0
        ? configuration.warningText ? [configuration.warningText(differences, text)]
          : [text('sync.clearWarning'), clears.map((field) => configuration.fieldName(field, text)).join(', ')] : [])].join(' ');
    return <Tooltip title={orderId ? text('sync.orderId', { id: orderId })
      : [tooltip, ...(allowed && rowWarning ? [rowWarning] : [])].join(' ')}>
      <span><Button size="small" variant="contained" disableElevation
        disabled={!allowed || busy || !!orderId} color={hasWarning ? 'warning' : 'primary'}
        startIcon={hasWarning ? <Icon>warning_amber</Icon> : undefined}
        onClick={(event) => { event.stopPropagation(); open(row); }}>
        {text(orderId ? 'sync.savedButton' : hasWarning ? 'sync.warningButton' : 'sync.button')}
      </Button></span>
    </Tooltip>;
  };
  const canSync = (row: TRow) => configuration.visible(row) && configuration.blockReason(row) == null &&
    !recorded[rowKey(row)];
  return { action, dialog, canSync };
}
